/**
 * API Configuration
 * Centralized configuration for all API endpoints
 */

// Base URL configuration
// In development on localhost, use proxy (/ris) to avoid CORS
// Otherwise, use full URL (http://20.244.40.31:5000)
//const isDevelopment = process.env.NODE_ENV === 'development';
//const isLocalhost =
// typeof window !== 'undefined' &&
//(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Use relative URL only in development AND on localhost (where proxy works)
// Otherwise, use full URL to ensure it works on VM/remote servers
//const RIS_API_BASE_URL =
//isDevelopment && isLocalhost
//? '' // Use relative URL for proxy in development on localhost
// : process.env.RIS_API_BASE_URL || 'http://20.244.40.31:5000';

//const API_BASE_URL = isDevelopment
//  ? '' // Use relative URL for proxy in development
//  : process.env.API_BASE_URL || 'https://qa.labsaarthi.com';

// Base URL configuration
// For VM / remote deployment, always use full URLs

export const RIS_API_BASE_URL = process.env.RIS_API_BASE_URL || 'https://ris.qa.labsaarthi.com/api';

/** QIDO-RS root — must match imaging worklist `qidoRoot` (DICOM_QIDO_ROOT env / default.js). */
export const DICOM_QIDO_ROOT = (
  process.env.DICOM_QIDO_ROOT || 'https://ris.qa.labsaarthi.com/ris/api/dicomweb'
).replace(/\/$/, '');

const DICOMWEB_STUDY_INCLUDE_FIELDS = '00081030,00080060';

/** Build `/studies?StudyInstanceUID=…` on the same DICOMweb host as the worklist. */
export function buildDicomWebStudiesByUidUrl(studyInstanceUid: string): string {
  const params = new URLSearchParams({
    StudyInstanceUID: studyInstanceUid,
    limit: '1',
    fuzzymatching: 'true',
    includefield: DICOMWEB_STUDY_INCLUDE_FIELDS,
  });
  return `${DICOM_QIDO_ROOT}/studies?${params.toString()}`;
}

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: `${RIS_API_BASE_URL}/ris/api/auth/login`,
    PATIENT_LOGIN: `${RIS_API_BASE_URL}/ris/api/auth/patient/login`,
    TOKEN: `${RIS_API_BASE_URL}/auth/realms/radiology/protocol/openid-connect/token`,
    LOGOUT: `${RIS_API_BASE_URL}/auth/realms/radiology/protocol/openid-connect/logout`,
    REFRESH: `${RIS_API_BASE_URL}/auth/realms/radiology/protocol/openid-connect/token`,
    USER_INFO: `${RIS_API_BASE_URL}/auth/realms/radiology/protocol/openid-connect/userinfo`,
    UPDATE_PASSWORD: `${RIS_API_BASE_URL}/ris/api/auth/update-password`,
    GET_CURRENT_USER: `${RIS_API_BASE_URL}/ris/api/auth/me`,
  },

  // Facility endpoints
  FACILITIES: {
    // GET all facilities: /ris/api/facilities/
    // GET single facility: /ris/api/facilities/{id}
    GET: (id?: string | number) =>
      id
        ? `${RIS_API_BASE_URL}/ris/api/facilities/${id}`
        : `${RIS_API_BASE_URL}/ris/api/facilities/`,
    CREATE: `${RIS_API_BASE_URL}/ris/api/facilities`,
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/facilities/${id}`,
    DELETE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/facilities/${id}`,
  },

  // User endpoints
  USERS: {
    // GET all users: /ris/api/users
    GET: `${RIS_API_BASE_URL}/ris/api/users`,
    // GET single user: /ris/api/users/{id}
    GET_BY_ID: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/users/${id}`,
    // CREATE user: /ris/api/auth/register
    CREATE: `${RIS_API_BASE_URL}/ris/api/auth/register`,
    // UPDATE user: /ris/api/users/{id}
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/users/${id}`,
    // DELETE user: /ris/api/users/{id}
    DELETE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/users/${id}`,
  },

  // Template endpoints (per-tenant)
  TEMPLATES: {
    // GET all templates: /ris/api/templates
    GET: `${RIS_API_BASE_URL}/ris/api/templates`,
    // GET single template: /ris/api/templates/{id}
    GET_BY_ID: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/templates/${id}`,
    // CREATE template: /ris/api/templates
    CREATE: `${RIS_API_BASE_URL}/ris/api/templates`,
    // UPDATE template: /ris/api/templates/{id}
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/templates/${id}`,
    /** Active/inactive: POST body `{ status, template_id }` or `{ status, template_ids }` */
    STATUS: `${RIS_API_BASE_URL}/ris/api/templates/status`,
    /** GET master catalog (platform templates for ris-radiologist import) */
    MASTER_CATALOG: `${RIS_API_BASE_URL}/ris/api/templates/master-catalog`,
    /** Bulk import from master catalog — POST same path as CREATE with `{ master_template_ids: number[] }` */
    IMPORT_MASTER_CATALOG: `${RIS_API_BASE_URL}/ris/api/templates`,
  },

  // Master (global) catalog for ris-super-admin — e.g. GET /ris/api/master-templates?page=1&limit=10
  MASTER_TEMPLATES: {
    GET: `${RIS_API_BASE_URL}/ris/api/master-templates`,
    GET_BY_ID: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/master-templates/${id}`,
    CREATE: `${RIS_API_BASE_URL}/ris/api/master-templates`,
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/master-templates/${id}`,
    DELETE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/master-templates/${id}`,
  },

  /** Logged-in patient: signed report PDF blob URL for current visit */
  PATIENTS: {
    ME_REPORT_BLOB: `${RIS_API_BASE_URL}/ris/api/patients/me/report-blob`,
    /** Optional `lang=en|hi` — Hindi returns translated narrative (mock or Azure) */
    ME_REPORT_CONTENT: `${RIS_API_BASE_URL}/ris/api/patients/me/report-content`,
  },

  // Report endpoints
  REPORTS: {
    /** Staff: signed report PDF blob URL for a visit (query: ris_visit_id) */
    BLOB: (risVisitId: string) =>
      `${RIS_API_BASE_URL}/ris/api/reports/blob?ris_visit_id=${encodeURIComponent(risVisitId)}`,
    // GET all reports: /ris/api/reports
    GET: `${RIS_API_BASE_URL}/ris/api/reports`,
    // GET single report: /ris/api/reports/{id}
    GET_BY_ID: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/reports/${id}`,
    /**
     * GET report by study instance UID: /ris/api/reports/study/{study_instance_uid}
     * Versioning (ris-backend reportVersionService): `data.versions[]`, `data.current_version_id`,
     * `data.approved_version_fk`, `data.approved_version_number`.
     */
    GET_BY_STUDY_UID: (studyInstanceUID: string) =>
      `${RIS_API_BASE_URL}/ris/api/reports/study/${studyInstanceUID}`,
    // Public report verification certificate (no auth): /ris/api/reports/public/study/{study_instance_uid}
    PUBLIC_VERIFY_BY_STUDY_UID: (studyInstanceUID: string) =>
      `${RIS_API_BASE_URL}/ris/api/reports/public/study/${encodeURIComponent(studyInstanceUID)}`,
    /**
     * CREATE report: /ris/api/reports
     * First persist creates v1. Body may include `selected_version_id` on later updates (ignored on create).
     */
    CREATE: `${RIS_API_BASE_URL}/ris/api/reports`,
    /**
     * UPDATE report: /ris/api/reports/{id}
     * Body may include `selected_version_id` so finalize/save uses that snapshot when content is unchanged.
     * Content changes append the next version (ris-backend persistReportVersion).
     */
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/reports/${id}`,
    // DELETE report: /ris/api/reports/{id}
    DELETE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/reports/${id}`,
  },

  // Anatomy parts endpoints
  ANATOMY_PARTS: {
    // GET all anatomy parts: /ris/api/anatomy-parts
    GET: `${RIS_API_BASE_URL}/ris/api/anatomy-parts`,
  },

  // Modalities endpoints
  MODALITIES: {
    // GET all modalities: /ris/api/modalities
    GET: `${RIS_API_BASE_URL}/ris/api/modalities`,
  },

  // PACS imaging machine registry (RIS proxies to DICOM_WADO_ROOT + /machines/…)
  PACS_MACHINES: {
    LIST: `${RIS_API_BASE_URL}/ris/api/pacs/machines`,
    GET_BY_ID: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/pacs/machines/${id}`,
    CREATE: `${RIS_API_BASE_URL}/ris/api/pacs/machines`,
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/pacs/machines/${id}`,
    ACTIVATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/pacs/machines/${id}/activate`,
    DELETE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/pacs/machines/${id}`,
    CHECK_CONNECTIVITY: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/pacs/machines/${id}/check-connectivity`,
    MAINTENANCE_LIST: (params?: {
      offset?: number;
      limit?: number;
      orderby?: string;
      orderdesc?: boolean;
    }) => {
      const queryParams = new URLSearchParams();
      queryParams.set('offset', String(params?.offset ?? 0));
      queryParams.set('limit', String(params?.limit ?? 50));
      queryParams.set('orderby', params?.orderby ?? 'startTime');
      queryParams.set('orderdesc', String(params?.orderdesc ?? true));
      return `${RIS_API_BASE_URL}/ris/api/pacs/machines/maintenance?${queryParams.toString()}`;
    },
    SCHEDULE_MAINTENANCE: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/pacs/machines/${id}/maintenance`, // → {DICOM_WADO_ROOT}/machines/{pk}/maintenance
    UPDATE_MAINTENANCE: (machineId: string | number, maintenanceId: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/pacs/machines/${machineId}/maintenance/${maintenanceId}`,
    CANCEL_MAINTENANCE: (machineId: string | number, maintenanceId: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/pacs/machines/${machineId}/maintenance/${maintenanceId}/cancel`,
  },

  PACS_CONNECTIVITY: {
    CHECK_BY_AE_TITLE: (aeTitle: string) =>
      `${RIS_API_BASE_URL}/ris/api/pacs/connectivity/check/${encodeURIComponent(aeTitle)}`,
  },

  // Studies endpoints
  STUDIES: {
    // Assign study to radiologist: /ris/api/studies/assign (POST)
    ASSIGN: `${RIS_API_BASE_URL}/ris/api/studies/assign`,
    // Reassign study to radiologist: /ris/api/studies (PUT)
    REASSIGN: `${RIS_API_BASE_URL}/ris/api/studies`,
    // Assign studies from SPOKE to linked HUB facility: /ris/api/studies/assign-hub (POST)
    ASSIGN_HUB: `${RIS_API_BASE_URL}/ris/api/studies/assign-hub`,
    // Reassign / unassign hub routing for SPOKE: /ris/api/studies/hub-routing (POST)
    HUB_ROUTING: `${RIS_API_BASE_URL}/ris/api/studies/hub-routing`,
    // Mark study as critical: /ris/api/studies/mark-critical (PUT)
    MARK_CRITICAL: `${RIS_API_BASE_URL}/ris/api/studies/mark-critical`,
  },

  // DICOMweb + document proxy (QIDO uses DICOM_QIDO_ROOT; same host as worklist studies list)
  DICOMWEB: {
    QIDO_ROOT: DICOM_QIDO_ROOT,
    STUDIES_BY_UID: buildDicomWebStudiesByUidUrl,
    PATIENT_DOCUMENT: `${RIS_API_BASE_URL}/ris/api/dicomweb/patient-document`,
  },

  // Dashboard endpoints (admin dashboard stats)
  DASHBOARD: {
    STATS: `${RIS_API_BASE_URL}/ris/api/dashboard/stats`,
  },

  // Billing analytics (collection reconciliation)
  BILLING_ANALYTICS: {
    RECONCILIATION: `${RIS_API_BASE_URL}/ris/api/billing-analytics/reconciliation`,
    RECONCILIATION_EXPORT: `${RIS_API_BASE_URL}/ris/api/billing-analytics/reconciliation/export`,
  },

  // DICOM routing rules (listener ingest routing)
  DICOM_ROUTING_RULES: {
    GET: (params?: { page?: number }) => {
      const queryParams = new URLSearchParams();
      if (params?.page != null) {
        queryParams.append('page', String(params.page));
      }
      const queryString = queryParams.toString();
      return queryString
        ? `${RIS_API_BASE_URL}/ris/api/dicom-routing-rules?${queryString}`
        : `${RIS_API_BASE_URL}/ris/api/dicom-routing-rules`;
    },
    CREATE: `${RIS_API_BASE_URL}/ris/api/dicom-routing-rules`,
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/dicom-routing-rules/${id}`,
    DELETE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/dicom-routing-rules/${id}`,
    REORDER: `${RIS_API_BASE_URL}/ris/api/dicom-routing-rules/reorder`,
  },

  // Study assignment rules (facility auto-assignment on study create)
  STUDY_ASSIGNMENT_RULES: {
    LIST: (facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      return queryString
        ? `${RIS_API_BASE_URL}/ris/api/study-assignment-rules?${queryString}`
        : `${RIS_API_BASE_URL}/ris/api/study-assignment-rules`;
    },
    CREATE: `${RIS_API_BASE_URL}/ris/api/study-assignment-rules`,
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/study-assignment-rules/${id}`,
    DELETE: (id: string | number, facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      const base = `${RIS_API_BASE_URL}/ris/api/study-assignment-rules/${id}`;
      return queryString ? `${base}?${queryString}` : base;
    },
    REORDER: `${RIS_API_BASE_URL}/ris/api/study-assignment-rules/reorder`,
  },

  // Worklist priority rules (facility worklist ordering)
  WORKLIST_PRIORITY_RULES: {
    LIST: (facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      return queryString
        ? `${RIS_API_BASE_URL}/ris/api/worklist-priority-rules?${queryString}`
        : `${RIS_API_BASE_URL}/ris/api/worklist-priority-rules`;
    },
    CREATE: `${RIS_API_BASE_URL}/ris/api/worklist-priority-rules`,
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/worklist-priority-rules/${id}`,
    DELETE: (id: string | number, facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      const base = `${RIS_API_BASE_URL}/ris/api/worklist-priority-rules/${id}`;
      return queryString ? `${base}?${queryString}` : base;
    },
    REORDER: `${RIS_API_BASE_URL}/ris/api/worklist-priority-rules/reorder`,
  },

  // Patient access link validity (hours after latest SMS/WhatsApp dispatch)
  PATIENT_ACCESS_LINK_VALIDITY: {
    LIST: (facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      return queryString
        ? `${RIS_API_BASE_URL}/ris/api/patient-access-link-validity?${queryString}`
        : `${RIS_API_BASE_URL}/ris/api/patient-access-link-validity`;
    },
    CREATE: `${RIS_API_BASE_URL}/ris/api/patient-access-link-validity`,
    UPDATE: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/patient-access-link-validity/${id}`,
    DELETE: (id: string | number, facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      const base = `${RIS_API_BASE_URL}/ris/api/patient-access-link-validity/${id}`;
      return queryString ? `${base}?${queryString}` : base;
    },
  },

  NOTIFICATION_RETRY_CONFIG: {
    LIST: (facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      return queryString
        ? `${RIS_API_BASE_URL}/ris/api/notification-retry-config?${queryString}`
        : `${RIS_API_BASE_URL}/ris/api/notification-retry-config`;
    },
    CREATE: `${RIS_API_BASE_URL}/ris/api/notification-retry-config`,
    UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/notification-retry-config/${id}`,
    DELETE: (id: string | number, facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      const base = `${RIS_API_BASE_URL}/ris/api/notification-retry-config/${id}`;
      return queryString ? `${base}?${queryString}` : base;
    },
  },

  // Appointment reminder (hours before scheduled appointment to resend booking SMS)
  APPOINTMENT_REMINDER_CONFIG: {
    LIST: (facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      return queryString
        ? `${RIS_API_BASE_URL}/ris/api/appointment-reminder-config?${queryString}`
        : `${RIS_API_BASE_URL}/ris/api/appointment-reminder-config`;
    },
    CREATE: `${RIS_API_BASE_URL}/ris/api/appointment-reminder-config`,
    UPDATE: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/appointment-reminder-config/${id}`,
    DELETE: (id: string | number, facilityId?: number) => {
      const queryParams = new URLSearchParams();
      if (facilityId != null) {
        queryParams.append('facility_id', String(facilityId));
      }
      const queryString = queryParams.toString();
      const base = `${RIS_API_BASE_URL}/ris/api/appointment-reminder-config/${id}`;
      return queryString ? `${base}?${queryString}` : base;
    },
  },

  // Radiology tests (study types with pricing)
  RADIOLOGY_TESTS: {
    WITH_PRICES: (params: {
      facility_id: string | number;
      page?: number;
      limit?: number;
      modality_id?: number;
    }) => {
      const queryParams = new URLSearchParams();
      queryParams.append('facility_id', String(params.facility_id));
      if (params.page != null) {
        queryParams.append('page', String(params.page));
      }
      if (params.limit != null) {
        queryParams.append('limit', String(params.limit));
      }
      if (params.modality_id != null) {
        queryParams.append('modality_id', String(params.modality_id));
      }
      return `${RIS_API_BASE_URL}/ris/api/radiology-tests/with-prices?${queryParams.toString()}`;
    },
    TEST_NAMES: (params: {
      facility_id: string | number;
      modality_id?: number;
      active_only?: boolean;
    }) => {
      const queryParams = new URLSearchParams();
      queryParams.append('facility_id', String(params.facility_id));
      if (params.modality_id != null) {
        queryParams.append('modality_id', String(params.modality_id));
      }
      if (params.active_only != null) {
        queryParams.append('active_only', String(params.active_only));
      }
      return `${RIS_API_BASE_URL}/ris/api/radiology-tests/test-names?${queryParams.toString()}`;
    },
  },

  // RIS analytics dashboards
  ANALYTICS: {
    REPORT_DELIVERY: `${RIS_API_BASE_URL}/ris/api/analytics/report-delivery`,
    NOTIFICATION_DELIVERY: `${RIS_API_BASE_URL}/ris/api/analytics/notification-delivery`,
    PEER_REVIEW: `${RIS_API_BASE_URL}/ris/api/analytics/peer-review`,
    PEER_REVIEW_CASES: `${RIS_API_BASE_URL}/ris/api/analytics/peer-review/cases`,
    OPERATIONAL: `${RIS_API_BASE_URL}/ris/api/analytics/operational`,
    OPERATIONAL_EXPORT: `${RIS_API_BASE_URL}/ris/api/analytics/operational/export`,
    AI_DASHBOARD: `${RIS_API_BASE_URL}/ris/api/analytics/ai-dashboard`,
    AI_DASHBOARD_EXPORT: `${RIS_API_BASE_URL}/ris/api/analytics/ai-dashboard/export`,
    STATEWIDE_KPIS: `${RIS_API_BASE_URL}/ris/api/analytics/statewide-kpis`,
    STATEWIDE_KPIS_EXPORT: `${RIS_API_BASE_URL}/ris/api/analytics/statewide-kpis/export`,
    EXPORT_JOB: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/analytics/export-jobs/${id}`,
    EXPORT_JOB_DOWNLOAD: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/analytics/export-jobs/${id}/download`,
  },

  // Escalation contacts (Feature 933 Phase 1)
  ESCALATION_MATRIX: {
    CONTACTS: `${RIS_API_BASE_URL}/ris/api/escalation-matrix/contacts`,
    UPDATE_CONTACT: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/escalation-matrix/contacts/${id}`,
    PATCH_CONTACT_STATUS: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/escalation-matrix/contacts/${id}/status`,
    MATRIX: `${RIS_API_BASE_URL}/ris/api/escalation-matrix`,
    UPDATE_MATRIX: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/escalation-matrix/${id}`,
    GET_BY_BAND: (facilityId: string | number, criticalityBand: string) =>
      `${RIS_API_BASE_URL}/ris/api/escalation-matrix/${facilityId}/${criticalityBand}`,
    ACTIVATE: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/escalation-matrix/${id}/activate`,
  },

  ESCALATION_INSTANCES: {
    LIST: `${RIS_API_BASE_URL}/ris/api/escalation-instances`,
    SUMMARY: `${RIS_API_BASE_URL}/ris/api/escalation-instances/summary`,
    PENDING: `${RIS_API_BASE_URL}/ris/api/escalation-instances/pending`,
    DETAIL: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/escalation-instances/${id}`,
    EVENTS: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/escalation-instances/${id}/events`,
    ACKNOWLEDGE: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/escalation-instances/${id}/acknowledge`,
  },

  CRITICAL_RESULTS_DASHBOARD: {
    SUMMARY: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/summary`,
    CRITICAL_ALERTS: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/critical-alerts`,
    OPEN_ALERTS: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/open-alerts`,
    PENDING_ACKNOWLEDGEMENTS: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/pending-acknowledgements`,
    ESCALATED_ALERTS: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/escalated-alerts`,
    SLA_BREACHES: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/sla-breaches`,
    ACKNOWLEDGED_ALERTS: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/acknowledged-alerts`,
    CANCELLED_ALERTS: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/cancelled-alerts`,
    DELIVERY_FAILED_ALERTS: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/delivery-failed-alerts`,
    ESCALATION_SUMMARY: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/escalation-summary`,
    TAT_METRICS: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/tat-metrics`,
    EXPORT: `${RIS_API_BASE_URL}/ris/api/critical-results-dashboard/export`,
  },

  CRITICAL_NOTIFICATIONS: {
    DETAIL: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/critical-notifications/${id}`,
    PUBLIC_STATUS: `${RIS_API_BASE_URL}/ris/api/critical-notifications/public/status`,
    ACKNOWLEDGE: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/critical-notifications/${id}/acknowledge`,
    PUBLIC_ACKNOWLEDGE: `${RIS_API_BASE_URL}/ris/api/critical-notifications/public/acknowledge`,
    HISTORY: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/critical-notifications/${id}/history`,
    REMINDER_HISTORY: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/critical-notifications/${id}/reminder-history`,
    ESCALATION_HISTORY: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/critical-notifications/${id}/escalation-history`,
    AUDIT_TIMELINE: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/critical-notifications/${id}/audit-timeline`,
  },

  ACKNOWLEDGEMENT_AUDIT_TRAIL: {
    SEARCH: `${RIS_API_BASE_URL}/ris/api/acknowledgement-audit-trail`,
    EXPORT: `${RIS_API_BASE_URL}/ris/api/acknowledgement-audit-trail/export`,
    DETAIL: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/acknowledgement-audit-trail/${id}`,
    TIMELINE: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/acknowledgement-audit-trail/${id}/timeline`,
  },

  // Report approval OTP (radiologist finalize & sign)
  APPROVAL_OTP: {
    GENERATE: `${RIS_API_BASE_URL}/ris/api/approval-otp/generate`,
    VERIFY: `${RIS_API_BASE_URL}/ris/api/approval-otp/verify`,
  },

  // Radiologist endpoints
  RADIOLOGISTS: {
    // GET all radiologists: /ris/api/users?role=Radiologist&status=Active
    // Parameters: page, limit, role, status, facility_id, reporting_modality_access, search, name
    GET: (params?: {
      page?: number;
      limit?: number;
      role?: string;
      status?: string;
      facility_id?: number;
      reporting_modality_access?: string;
      search?: string;
      name?: string;
      role_required?: string;
    }) => {
      const baseUrl = `${RIS_API_BASE_URL}/ris/api/users`;
      const queryParams = new URLSearchParams();

      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params?.role) {
        queryParams.append('role', params.role);
      }
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.facility_id) {
        queryParams.append('facility_id', params.facility_id.toString());
      }
      if (params?.reporting_modality_access) {
        queryParams.append('reporting_modality_access', params.reporting_modality_access);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.name) {
        queryParams.append('name', params.name);
      }
      if (params?.role_required) {
        queryParams.append('role_required', params.role_required);
      }

      const queryString = queryParams.toString();
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    },
    // Assign radiologist to studies
    // TODO: Replace with actual endpoint URL when provided
    // Expected payload: { radiologistId: number, studyIds: string[] }
    ASSIGN: `${RIS_API_BASE_URL}/ris/api/radiologists/assign`,
  },

  TAT_NOTIFICATIONS: {
    LIST: `${RIS_API_BASE_URL}/ris/api/tat-notifications`,
    UNREAD_COUNT: `${RIS_API_BASE_URL}/ris/api/tat-notifications/unread-count`,
    MARK_READ: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/tat-notifications/${id}/read`,
    MARK_ALL_READ: `${RIS_API_BASE_URL}/ris/api/tat-notifications/mark-all-read`,
    RUN_CYCLE: `${RIS_API_BASE_URL}/ris/api/tat-notifications/run-cycle`,
  },

  // Peer review escalation matrix (Admin RIS Config)
  PEER_REVIEW_MATRIX: {
    GET: (params?: { facility_id?: number }) => {
      const baseUrl = `${RIS_API_BASE_URL}/ris/api/peer-review-matrix`;
      if (params?.facility_id != null) {
        return `${baseUrl}?facility_id=${params.facility_id}`;
      }
      return baseUrl;
    },
    UPSERT: `${RIS_API_BASE_URL}/ris/api/peer-review-matrix`,
  },

  PEER_REVIEW_NOTIFICATIONS: {
    LIST: `${RIS_API_BASE_URL}/ris/api/peer-review-notifications`,
    UNREAD_COUNT: `${RIS_API_BASE_URL}/ris/api/peer-review-notifications/unread-count`,
    MARK_READ: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/peer-review-notifications/${id}/read`,
    MARK_ALL_READ: `${RIS_API_BASE_URL}/ris/api/peer-review-notifications/mark-all-read`,
  },

  // Peer review (second-reader assignment and assessment)
  PEER_REVIEWS: {
    LIST: (params?: {
      status?: string;
      study_instance_uid?: string;
      peer_reviewer_id?: number;
      search?: string;
      modality?: string;
      priority?: string;
      needs_action?: string;
      workflow_id?: number;
      page?: number;
      limit?: number;
    }) => {
      const baseUrl = `${RIS_API_BASE_URL}/ris/api/peer-reviews`;
      const queryParams = new URLSearchParams();
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.study_instance_uid) {
        queryParams.append('study_instance_uid', params.study_instance_uid);
      }
      if (params?.peer_reviewer_id != null) {
        queryParams.append('peer_reviewer_id', String(params.peer_reviewer_id));
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.modality) {
        queryParams.append('modality', params.modality);
      }
      if (params?.priority) {
        queryParams.append('priority', params.priority);
      }
      if (params?.needs_action) {
        queryParams.append('needs_action', params.needs_action);
      }
      if (params?.workflow_id != null) {
        queryParams.append('workflow_id', String(params.workflow_id));
      }
      if (params?.page != null) {
        queryParams.append('page', String(params.page));
      }
      if (params?.limit != null) {
        queryParams.append('limit', String(params.limit));
      }
      const queryString = queryParams.toString();
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    },
    SUMMARY: `${RIS_API_BASE_URL}/ris/api/peer-reviews/summary`,
    ASSIGN: `${RIS_API_BASE_URL}/ris/api/peer-reviews/assign`,
    GET_BY_ID: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/peer-reviews/${id}`,
    GET_BY_STUDY_UID: (studyInstanceUid: string) =>
      `${RIS_API_BASE_URL}/ris/api/peer-reviews/study/${encodeURIComponent(studyInstanceUid)}`,
    HISTORY_BY_STUDY_UID: (studyInstanceUid: string) =>
      `${RIS_API_BASE_URL}/ris/api/peer-reviews/study/${encodeURIComponent(studyInstanceUid)}/history`,
    SUBMIT: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/peer-reviews/${id}/submit`,
    COMPARE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/peer-reviews/${id}/compare`,
  },

  // AI study analyses + radiologist review (RISPACS-1036)
  AI_STUDY_ANALYSES: {
    BY_STUDY: (studyInstanceUid: string) =>
      `${RIS_API_BASE_URL}/ris/api/ai-study-analyses/by-study/${encodeURIComponent(studyInstanceUid)}`,
    SUBMIT_REVIEW: (id: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/ai-study-analyses/${id}/review`,
    GENERATE_DRAFT_REPORT: `${RIS_API_BASE_URL}/ris/api/ai-study-analyses/draft-report`,
  },

  /** ABDM M3 HIU consents — RIS adapter (radiologist-only) */
  ABDM_CONSENTS: {
    LIST: (params?: {
      abhaAddress?: string;
      drName?: string;
      name?: string;
      hiTypes?: string[] | string;
      from?: string;
      to?: string;
      page?: number;
    }) => {
      const queryParams = new URLSearchParams();
      if (params?.abhaAddress) {
        queryParams.set('abhaAddress', params.abhaAddress);
      }
      if (params?.drName) {
        queryParams.set('drName', params.drName);
      }
      if (params?.name) {
        queryParams.set('name', params.name);
      }
      if (params?.hiTypes) {
        const hi = Array.isArray(params.hiTypes)
          ? params.hiTypes.join(',')
          : String(params.hiTypes);
        if (hi) {
          queryParams.set('hiTypes', hi);
        }
      }
      if (params?.from) {
        queryParams.set('from', params.from);
      }
      if (params?.to) {
        queryParams.set('to', params.to);
      }
      if (params?.page != null) {
        queryParams.set('page', String(params.page));
      }
      const qs = queryParams.toString();
      const base = `${RIS_API_BASE_URL}/ris/api/abdm/consents`;
      return qs ? `${base}?${qs}` : base;
    },
    CREATE: `${RIS_API_BASE_URL}/ris/api/abdm/consents`,
    GET_BY_ID: (sessionId: string) =>
      `${RIS_API_BASE_URL}/ris/api/abdm/consents/${encodeURIComponent(sessionId)}`,
    ATTACHMENT: (sessionId: string, bundleId: string | number, num: string | number) =>
      `${RIS_API_BASE_URL}/ris/api/abdm/consents/${encodeURIComponent(sessionId)}/attachments/${encodeURIComponent(String(bundleId))}/${encodeURIComponent(String(num))}`,
  },
};

// API Client configuration
export const API_CONFIG = {
  CLIENT_ID: 'iqline-redio',
  GRANT_TYPE: 'password',
  REFRESH_GRANT_TYPE: 'refresh_token',
  TIMEOUT: 30000, // 30 seconds
  TENANT_ID: '700', // Tenant ID for API requests
};

// Token storage keys (RIS auth uses localStorage; patient auth uses sessionStorage)
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_EXPIRES_AT: 'token_expires_at',
  USER_DATA: 'user_data',
  FACILITY_ID: 'facility_id',
  MODALITY: 'modality',
  FACILITY: 'facility',
  MODALITIES: 'modalities', // Stored modalities from API
  HAS_RADIOLOGIST_HIERARCHY: 'has_radiologist_hierarchy',
  IS_PATIENT_MODE: 'is_patient_mode',
  PATIENT_DICOM_ID: 'patient_dicom_id',
  PATIENT_AGE: 'patient_age',
  PATIENT_GENDER: 'patient_gender',
  /** ISO timestamp until which OTP-verified approval session is valid (radiologist only, sessionStorage) */
  APPROVAL_SESSION_VALID_UNTIL: 'approval_session_valid_until',
};

export default {
  API_ENDPOINTS,
  API_CONFIG,
  STORAGE_KEYS,
};
